import type { BrandKitType } from 'types'
import { generateImage } from 'ui/helpers/image'
import { invert, rgb2hex } from './helpers/pallet'

function addAlpha(color = '', opacity = 0) {
  // coerce values so it is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity ?? 1, 0), 1) * 255)
  return color + _opacity.toString(16).toUpperCase()
}

function getHeaderImageUrl(image: string) {
  if (!image) return ''
  if (/\.(?:gif|svg)$/i.test(image)) return image

  return generateImage(image, 'header')
}

function cssUrl(url: string) {
  if (!url) return 'none'

  const escapedUrl = url.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `url("${escapedUrl}")`
}

export function BrandKitStyles({
  brand,
  ignoreBody = false,
  bridgeTokens = true,
  brandSkeletons = true,
}: {
  brand: BrandKitType
  ignoreBody?: boolean
  /**
   * Inject the v4 design-system token bridge (`--primary` / `--muted-foreground`
   * / `--border` / … derived from the venue palette). **Default on** for genuine
   * guest-facing previews. Pass `false` at any layout/root mount — injecting it
   * globally bleeds venue colours into every CRM control and overrides the
   * design-system `:root` tokens (washed text, brand-tinted borders).
   */
  bridgeTokens?: boolean
  /**
   * Emit the `--brand-skeleton-*` tints (brand-coloured loading
   * skeletons). **Default on** for guest-facing surfaces. Pass `false`
   * at CRM layout/root mounts — a global mount turns every admin
   * loading screen the venue/brand colour (the pink skeleton pages).
   * With the vars unset, `<Skeleton>` falls back to its neutral greys.
   */
  brandSkeletons?: boolean
}) {
  const isBackgroundTransparent =
    (brand?.background ?? 'transparent') === 'transparent'

  if (!brand) {
    return null
  }

  // Venue brand → v4 token bridge. Every surface other than the page
  // canvas (`--background` / `--foreground`, fixed in app globals)
  // is driven by venue inputs so cards, popovers, accents, borders
  // and the primary CTA all reflect the venue's palette.
  const brandColor = brand.interfaceColor ?? '#ff3a9b'
  const backgroundColor =
    brand.background && brand.background !== 'transparent'
      ? brand.background
      : '#ffffff'
  const headerImage = brand.headerImage?.trim() ?? ''
  const headerImageUrl = getHeaderImageUrl(headerImage)
  const foregroundColor = invert(backgroundColor, true)
  const onBrand = invert(brandColor, true)

  // NB: inject via dangerouslySetInnerHTML, not as a text child of <style>.
  // A text child gets HTML-entity-escaped on the server (url("…") → url(&quot;…&quot;))
  // but not in the client DOM, which throws a hydration mismatch for every
  // brand whose asset URLs contain quotes.
  const css = `:root {
        --background-image: ${brand.backgroundImage};
        --portal-loading-background: ${backgroundColor};
        --brand-header-image: ${cssUrl(headerImageUrl)};
        --brand-color: ${brandColor};${
          brandSkeletons
            ? `
        --brand-skeleton-base: ${addAlpha(brandColor, 0.1)};
        --brand-skeleton-highlight: ${addAlpha(brandColor, 0.2)};
        --brand-skeleton-border: ${addAlpha(brandColor, 0.14)};`
            : ''
        }
        --background-color: ${brand.background};
        --background-invert-color: ${invert(brand.background ?? '#ffffff', true)};
        --brand-invert-color: ${invert(brand.interfaceColor ?? '#ff3a9b')};
        --brand-or-black: ${invert(brand.interfaceColor ?? '#ffffff', true) === '#000000' ? '#000000' : brand.interfaceColor};
        --body-font-family: ${brand.body_font_family || 'system-ui, -apple-system, sans-serif'};
        --heading-font-family: ${brand.heading_font_family || 'system-ui, -apple-system, sans-serif'};
        --brand-button-border-radius: ${
          brand.roundInputs ? '9999px' : '0.125rem'
        };
        --brand-border-radius: ${brand.roundInputs ? '1rem' : '0px'};
        --label-color: ${!ignoreBody ? addAlpha(invert(brand?.background ?? '#ffffff', true), 0.5) : 'inherit'};${
          bridgeTokens
            ? `

        /* tailwind-v4 token bridge — inset accent surfaces reflect the venue
           palette. GUEST-FACING PREVIEWS ONLY. Never enable at a layout/root:
           it bleeds venue colours into every CRM control and overrides the
           design-system :root tokens (washed text, brand-tinted borders). */
        --primary: ${brandColor};
        --primary-foreground: ${onBrand};
        --secondary: ${addAlpha(foregroundColor, 0.06)};
        --secondary-foreground: ${foregroundColor};
        --muted: ${addAlpha(foregroundColor, 0.04)};
        --muted-foreground: ${addAlpha(foregroundColor, 0.6)};
        --accent: ${addAlpha(foregroundColor, 0.06)};
        --accent-foreground: ${foregroundColor};
        --destructive: #e13d3d;
        --destructive-foreground: #ffffff;
        --border: ${addAlpha(foregroundColor, 0.1)};
        --input: ${addAlpha(foregroundColor, 0.1)};
        --ring: ${brandColor};
        --radius: ${brand.roundInputs ? '12px' : '4px'};`
            : ''
        }
      }
${
  brand.interface_pallet &&
  `

:root {
  --interface-color-50: ${brand.interface_pallet[50]};
  --interface-color-100: ${brand.interface_pallet[100]};
  --interface-color-200: ${brand.interface_pallet[200]};
  --interface-color-300: ${brand.interface_pallet[300]};
  --interface-color-400: ${brand.interface_pallet[400]};
  --interface-color-500: ${brand.interface_pallet[500]};
  --interface-color-600: ${brand.interface_pallet[600]};
  --interface-color-700: ${brand.interface_pallet[700]};
  --interface-color-800: ${brand.interface_pallet[800]};
  --interface-color-900: ${brand.interface_pallet[900]};
  --interface-color-default: ${brand.interface_pallet.DEFAULT};
}`
}

${
  brand.background_pallet &&
  `

:root {
  --background-color-50: ${brand.background_pallet[50]};
  --background-color-100: ${brand.background_pallet[100]};
  --background-color-200: ${brand.background_pallet[200]};
  --background-color-300: ${brand.background_pallet[300]};
  --background-color-400: ${brand.background_pallet[400]};
  --background-color-500: ${brand.background_pallet[500]};
  --background-color-600: ${brand.background_pallet[600]};
  --background-color-700: ${brand.background_pallet[700]};
  --background-color-800: ${brand.background_pallet[800]};
  --background-color-900: ${brand.background_pallet[900]};
  --background-color-default: ${brand.background_pallet.DEFAULT};
}`
}

${
  !ignoreBody &&
  `body:not(.in-frame) {
    background-color: ${brand.background};
  } 

  @media only screen and (max-width: 768px) {
${
  isBackgroundTransparent
    ? `:root {
  --background-color: #ffffff;
}`
    : ''
}

${
  !ignoreBody &&
  `
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h1,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h2,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h3,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h4,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h5,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) h6,
body:not(.in-frame):not(.in-frame-adam-ignore-unbarred) .font-heading {
      color: var( --background-invert-color);
         }
         *,:after,:before {
          border: 0 solid ${addAlpha(invert(brand?.background ?? '#ffffff', true), 0.15)};
         }
      `
}

  }
  
  body.in-frame h1,
  body.in-frame h2,
  body.in-frame h3,
  body.in-frame h4,
  body.in-frame h5,
  body.in-frame h6,
  body.in-frame .font-heading  {
    color: var( --background-invert-color);
  }
  body.in-frame *,
  body.in-frame  :after,
  body.in-frame  :before {
   // border: 0 solid ${addAlpha(invert(brand?.background ?? '#ffffff', true), 0.3)};
  }
  `
}

.in-frame .grid-rows-\\[50vh_1fr\\], .in-frame .grid-rows-\\[40vh_1fr\\] {
  grid-template-rows: auto;
}
      `

  return <style dangerouslySetInnerHTML={{ __html: css }} />
}

/**
 *   html, body {
       // font-family: var(--body-font-family);
      }
      h1, h2, h3, h4, h5, h6 {
       // font-family: var(--heading-font-family);
      }
 */
